import React, { useState } from 'react';
import { X, Sparkles, ThumbsUp, ThumbsDown, Plus } from 'lucide-react';
import Button from '../../../components/ui/Button';

const AIHazardSuggestions = ({ onClose }) => {
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);

  const aiSuggestions = [
    {
      id: 'AI-001',
      confidence: 95,
      hazard: 'Simultaneous hot work in adjacent areas',
      reasoning: 'Analysis of work permit patterns shows overlapping hot work activities in confined spaces within proximity. Historical data indicates 3 near-miss incidents with similar conditions.',
      suggestedControls: [
        'Implement hot work coordination protocol',
        'Extend isolation boundaries',
        'Additional fire watch personnel',
        'Enhanced gas testing frequency',
      ],
      riskLevel: 'high',
      similarIncidents: 3,
      location: 'Offshore Alpha - Module C',
    },
    {
      id: 'AI-002',
      confidence: 88,
      hazard: 'Pump cavitation during startup after maintenance',
      reasoning: 'Pattern recognition from maintenance logs and equipment monitoring data suggests increased risk of pump damage. Similar equipment in the fleet has experienced 5 cavitation events post-maintenance.',
      suggestedControls: [
        'Verify proper venting before startup',
        'Implement slower startup ramp rate',
        'Monitor vibration levels closely',
        'Pre-startup pressure verification',
      ],
      riskLevel: 'medium',
      similarIncidents: 5,
      location: 'Refinery 01 - Crude Unit',
    },
    {
      id: 'AI-003',
      confidence: 92,
      hazard: 'Hydrogen sulfide accumulation in low-lying areas',
      reasoning: 'Weather pattern analysis combined with gas detection history indicates favorable conditions for H2S accumulation. Wind speed and direction data suggest poor natural ventilation in specified areas.',
      suggestedControls: [
        'Increase gas detection point coverage',
        'Implement restricted access zones',
        'Mandatory buddy system with escape packs',
        'Enhanced ventilation before work',
      ],
      riskLevel: 'critical',
      similarIncidents: 2,
      location: 'Terminal West - Loading Area',
    },
    {
      id: 'AI-004',
      confidence: 79,
      hazard: 'Equipment stress from rapid temperature changes',
      reasoning: 'Thermal cycling analysis of process data shows temperature ramp rates exceeding design specifications during seasonal transitions. Metallurgical assessment suggests fatigue risk.',
      suggestedControls: [
        'Implement temperature ramp rate limits',
        'Enhanced thermal monitoring',
        'Pre-heat procedures for cold starts',
        'Thermal stress inspection schedule',
      ],
      riskLevel: 'medium',
      similarIncidents: 1,
      location: 'Offshore Bravo - Processing Module',
    },
  ];

  const getConfidenceColor = (confidence) => {
    if (confidence >= 90) return 'text-green-600 bg-green-50';
    if (confidence >= 75) return 'text-yellow-600 bg-yellow-50';
    return 'text-orange-600 bg-orange-50';
  };

  const getRiskBadgeColor = (level) => {
    const colors = {
      critical: 'bg-red-100 text-red-800 border-red-300',
      high: 'bg-orange-100 text-orange-800 border-orange-300',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      low: 'bg-green-100 text-green-800 border-green-300',
    };
    return colors?.[level] || colors?.medium;
  };

  const toggleSuggestion = (id) => {
    setSelectedSuggestions(prev =>
      prev?.includes(id) ? prev?.filter(sid => sid !== id) : [...prev, id]
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-purple-50 to-purple-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-600 rounded-lg">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">AI-Assisted Hazard Suggestions</h2>
              <p className="text-sm text-gray-600 mt-1">
                Machine learning analysis of operational data and historical incidents
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-purple-200 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {aiSuggestions?.map((suggestion) => (
              <div
                key={suggestion?.id}
                className={`
                  rounded-lg border-2 transition-all
                  ${selectedSuggestions?.includes(suggestion?.id)
                    ? 'border-purple-500 bg-purple-50' :'border-gray-200 bg-white'
                  }
                `}
              >
                {/* Suggestion Header */}
                <div className="p-6 space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-bold ${getConfidenceColor(suggestion?.confidence)}`}>
                          {suggestion?.confidence}% Confidence
                        </span>
                        <span className={`px-3 py-1 rounded border text-sm font-medium ${getRiskBadgeColor(suggestion?.riskLevel)}`}>
                          {suggestion?.riskLevel?.toUpperCase()} RISK
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">{suggestion?.hazard}</h3>
                      <p className="text-sm text-gray-600 mt-1">{suggestion?.id}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={selectedSuggestions?.includes(suggestion?.id)}
                      onChange={() => toggleSuggestion(suggestion?.id)}
                      className="w-5 h-5 text-purple-600 rounded mt-1"
                    />
                  </div>

                  {/* AI Reasoning */}
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <div className="text-sm font-semibold text-blue-900 mb-1">AI Analysis</div>
                        <p className="text-sm text-blue-800">{suggestion?.reasoning}</p>
                      </div>
                    </div>
                  </div>

                  {/* Location and Similar Incidents */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="text-xs font-medium text-gray-500 mb-1">Location</div>
                      <div className="text-sm font-semibold text-gray-900">{suggestion?.location}</div>
                    </div>
                    <div className="p-3 bg-orange-50 rounded-lg">
                      <div className="text-xs font-medium text-gray-500 mb-1">Similar Historical Incidents</div>
                      <div className="text-sm font-semibold text-orange-900">{suggestion?.similarIncidents} incidents found</div>
                    </div>
                  </div>

                  {/* Suggested Controls */}
                  <div>
                    <div className="text-sm font-semibold text-gray-900 mb-3">Recommended Control Measures</div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {suggestion?.suggestedControls?.map((control, idx) => (
                        <div key={idx} className="flex items-start gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                          <Plus className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-green-900">{control}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-4 border-t border-gray-200">
                    <Button size="sm" variant="outline" className="text-green-600 border-green-600 hover:bg-green-50">
                      <ThumbsUp className="w-4 h-4 mr-2" />
                      Good Suggestion
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-600 hover:bg-red-50">
                      <ThumbsDown className="w-4 h-4 mr-2" />
                      Not Relevant
                    </Button>
                    <Button size="sm" variant="outline">
                      View Similar Incidents
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="text-sm text-gray-600">
            {selectedSuggestions?.length} suggestion(s) selected
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700 text-white"
              disabled={selectedSuggestions?.length === 0}
            >
              Add Selected to HAZID ({selectedSuggestions?.length})
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIHazardSuggestions;